/**
 * The d3 pieces the Relationship Map engine draws its Territory map with, from the console's
 * own packages (the design loaded d3, topojson-client and us-atlas from a CDN). The engine
 * calls them under the same names as the design's global d3 / topojson.
 */
import { geoAlbersUsa, geoContains, geoMercator, geoPath } from 'd3-geo';
import { select } from 'd3-selection';
import 'd3-transition';
import { zoom, zoomIdentity } from 'd3-zoom';
import { feature, merge } from 'topojson-client';

/** d3.interpolateRgb (gamma 1): linear in sRGB, "rgb(r, g, b)" with rounded channels. */
export function interpolateRgb(a: string, b: string): (t: number) => string {
  const p = (c: string) => [parseInt(c.slice(1, 3), 16), parseInt(c.slice(3, 5), 16), parseInt(c.slice(5, 7), 16)];
  const A = p(a);
  const B = p(b);
  return (t: number) => {
    const ch = A.map((v, i) => Math.round(v + (B[i] - v) * t));
    return `rgb(${ch[0]}, ${ch[1]}, ${ch[2]})`;
  };
}

export const d3 = { select, zoom, zoomIdentity, geoMercator, geoAlbersUsa, geoPath, interpolateRgb };
export const topojson = { feature, merge };

/**
 * County FIPS for entities that carry a location but no county (catalog buildings have a
 * latitude and longitude from their CMS address): the us-atlas county that contains the
 * point. Fills `loc` (id -> FIPS) and `cities` (FIPS -> the first entity's city) in place,
 * never overwriting a county the back end already gave.
 */
export function locateCounties(
  nodes: Record<string, { lat?: number | null; lng?: number | null; fips?: string | null; city?: string | null }>,
  topo: unknown,
  loc: Record<string, string>,
  cities: Record<string, string>,
): void {
  const want = Object.keys(nodes).filter((id) => !loc[id] && nodes[id].lat != null && nodes[id].lng != null);
  if (!want.length || !topo) return;
  const t = topo as { objects: { counties: unknown } };
  const fc = feature(t as never, t.objects.counties as never) as unknown as { features: { id?: string | number }[] };
  for (const id of want) {
    const pt: [number, number] = [Number(nodes[id].lng), Number(nodes[id].lat)];
    const hit = fc.features.find((f) => geoContains(f as never, pt));
    if (!hit || hit.id == null) continue;
    const fips = String(hit.id);
    loc[id] = fips;
    if (!cities[fips] && nodes[id].city) cities[fips] = String(nodes[id].city);
  }
}

/** The us-atlas counties, loaded once as the console's own chunk. */
export const COUNTIES: { topo: unknown } = { topo: null };
let loading: Promise<unknown> | null = null;
export function loadCounties(): Promise<unknown> {
  loading ??= import('us-atlas/counties-10m.json').then((m) => {
    COUNTIES.topo = (m as { default?: unknown }).default ?? m;
    return COUNTIES.topo;
  });
  return loading;
}
